/**
 * 시각화 데이터 처리를 위한 헬퍼 유틸리티
 * App.js에서 분리된 시각화 관련 로직들을 포함
 */

/**
 * 차트 데이터 업데이트 함수
 * API에서 받은 데이터로 차트 업데이트
 * @param {Object} newData - 새로운 데이터
 * @param {Function} setChartData - 차트 데이터 설정 함수
 */
export const updateChartData = (newData, setChartData) => {
  if (newData && newData.visualization_created) {
    setChartData(newData.data);
  }
};

/**
 * 시각화 데이터 추가 함수
 * 중복 체크 및 고유 ID 생성을 포함한 시각화 데이터 추가
 * @param {Object} vizData - 시각화 데이터
 * @param {Function} setCurrentVisualizations - 현재 시각화 상태 설정 함수
 * @param {Object} visualizationsRef - 시각화 ref 객체
 * @param {boolean} isDeveloperMode - 개발자 모드 여부
 */
export const addVisualization = (vizData, setCurrentVisualizations, visualizationsRef, isDeveloperMode) => {
  // 시각화 데이터에 고유 ID 추가 (없는 경우)
  if (!vizData.id) {
    vizData.id = `viz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  // 함수형 업데이트 사용하여 최신 상태 보장
  setCurrentVisualizations(prev => {
    // ID 기반 중복 체크
    const isDuplicateId = prev.some(item => item.id === vizData.id);
    
    // 컨텐츠 기반 중복 체크 (ID가 다른 경우)
    const isDuplicateContent = prev.some(item => 
      item.id !== vizData.id && // ID가 다른 항목만 컨텐츠 비교
      JSON.stringify(item.data) === JSON.stringify(vizData.data) && 
      item.title === vizData.title
    );
    
    // 중복이 아닌 경우만 추가 - 새 메시지마다 시각화를 새로 시작
    if (!isDuplicateId && !isDuplicateContent) {
      // 개발자 모드일 때만 로그 출력
      if (isDeveloperMode) {
        console.log("시각화 데이터 추가:", vizData.title, "ID:", vizData.id, "현재 개수:", prev.length);
      }
      
      // 새 시각화 데이터만 추가하고 이전 데이터는 무시 (대체)
      const newVisualizations = [...prev, vizData];
      
      // ref도 함께 업데이트
      visualizationsRef.current = newVisualizations;
      return newVisualizations;
    }
    
    // 개발자 모드일 때만 로그 출력
    if (isDeveloperMode) {
      console.log("중복된 시각화 데이터 무시:", vizData.title, "ID:", vizData.id);
    }
    return prev;
  });
};

/**
 * 백엔드에서 받은 시각화 데이터를 처리하는 함수
 * @param {Object} eventData - 이벤트 데이터
 * @param {Function} setCurrentVisualizations - 현재 시각화 상태 설정 함수
 * @param {Function} setStreamingDisplayBlocks - 스트리밍 블록 상태 설정 함수
 * @param {Object} streamingDisplayBlocksRef - 스트리밍 블록 ref 객체
 * @param {Object} visualizationsRef - 시각화 ref 객체
 * @param {boolean} isDeveloperMode - 개발자 모드 여부
 * @returns {boolean} 시각화가 추가되었는지 여부
 */
export const processVisualizationData = (
  eventData, 
  setCurrentVisualizations, 
  setStreamingDisplayBlocks, 
  streamingDisplayBlocksRef, 
  visualizationsRef, 
  isDeveloperMode
) => {
  let visualizationAdded = false;
  try {
    const processViz = (vizData) => {
      if (vizData) {
        // 시각화 데이터에 고유 ID 추가 (없는 경우)
        if (!vizData.id) {
          vizData.id = `viz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        // 1. 최종 저장을 위한 상태 업데이트 (기존 로직)
        addVisualization(vizData, setCurrentVisualizations, visualizationsRef, isDeveloperMode); 

        // 2. UI 표시용 블록 배열에 시각화 블록 추가
        setStreamingDisplayBlocks(prevBlocks => {
          let newBlocks = prevBlocks;
          // 이미 동일한 ID의 블록이 있는지 확인 (중복 방지)
          if (!prevBlocks.some(block => block.type === 'visualization' && block.id === vizData.id)) {
            newBlocks = [...prevBlocks, { type: 'visualization', id: vizData.id, data: vizData }];
            streamingDisplayBlocksRef.current = newBlocks; // ref 업데이트
          }
          return newBlocks; // 상태 업데이트 (중복이면 변경 없음)
        });
        
        visualizationAdded = true; // 시각화 추가됨 플래그 설정
      }
    };

    const processDashboardComponent = (componentData) => {
      if (componentData) {
        // 대시보드 컴포넌트 데이터의 ID는 component_template.id를 사용
        // 새로운 ID를 생성하지 않고 백엔드에서 제공한 ID를 그대로 사용
        const componentId = componentData.component_template?.id || `dashboard_comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // UI 표시용 블록 배열에 대시보드 컴포넌트 블록 추가
        setStreamingDisplayBlocks(prevBlocks => {
          let newBlocks = prevBlocks;
          // 이미 동일한 component_template.id의 블록이 있는지 확인 (중복 방지)
          if (!prevBlocks.some(block => 
            block.type === 'dashboard_component' && 
            block.data?.component_template?.id === componentId
          )) {
            newBlocks = [...prevBlocks, { 
              type: 'dashboard_component', 
              id: componentId, 
              data: componentData 
            }];
            streamingDisplayBlocksRef.current = newBlocks; // ref 업데이트
            
            if (isDeveloperMode) {
              console.log("[DEBUG] 대시보드 컴포넌트 블록 추가:", componentData);
            }
          } else {
            if (isDeveloperMode) {
              console.log("[DEBUG] 중복된 대시보드 컴포넌트 블록 무시:", componentId);
            }
          }
          return newBlocks; // 상태 업데이트 (중복이면 변경 없음)
        });
        
        visualizationAdded = true; // 처리됨 플래그 설정
      }
    };

    // 새로운 시각화 데이터 이벤트 처리
    if (eventData.visualization_data === true && eventData.data) {
      try {
        // 백엔드에서 수정된 구조: eventData.data가 직접 시각화 데이터
        const vizData = typeof eventData.data === 'string' 
          ? JSON.parse(eventData.data) 
          : eventData.data;
        
        // dashboard_component 타입 처리
        if (vizData && vizData.type === 'dashboard_component') {
          if (isDeveloperMode) {
            console.log("[DEBUG] 대시보드 컴포넌트 데이터 처리:", vizData.data?.component_template?.title || "제목 없음");
          }
          processDashboardComponent(vizData.data);
        }
        // 기존 시각화 데이터 처리
        else if (vizData && vizData.viz_type) {
          if (isDeveloperMode) {
            console.log("[DEBUG] 시각화 데이터 처리:", vizData.title || "제목 없음");
          }
          processViz(vizData);
        }
      } catch (e) {
        console.error("시각화 데이터 파싱 오류:", e);
      }
    }
    
    // 하위 호환성을 위한 기존 메타데이터 처리 (필요시)
    if (eventData.type === 'tool_result' && eventData.data && eventData.data.result) {
      try {
        const resultObj = typeof eventData.data.result === 'string' 
          ? JSON.parse(eventData.data.result) 
          : eventData.data.result;
        
        // 도구 결과 메타데이터는 시각화 처리하지 않음 (필요시 개발자 모드에서만 표시)
        if (isDeveloperMode) {
          console.log("[DEBUG] 도구 결과 메타데이터 수신:", resultObj);
        }
      } catch (e) {
        console.error("도구 결과 메타데이터 파싱 오류:", e);
      }
    }
    
    // 하위 호환성을 위한 기존 metadata 처리 (필요시)
    if (eventData.type === 'metadata' && eventData.tool_result && eventData.tool_result.result) {
      // 로그만 남기고 처리하지 않음 (이전 코드와 호환성 유지)
      if (isDeveloperMode) {
        console.log("[DEBUG] 기존 메타데이터 감지 (미처리)");
      }
    }

  } catch (e) {
    console.error("시각화 데이터 처리 중 오류:", e);
  }
  return visualizationAdded;
}; 